import { ColumnConfig } from '.'
import { t } from 'translations'

// `section` and `name` below are identifiers (column names are persisted with the saved table layout);
// the visible labels come from getSectionLabel / getColumnName.
export const ORDERS_TABLE_COLUMNS_CONFIG = [
    {
        section: 'Order details',
        columns: [
            { name: 'Location', key: 'location' },
            { name: 'Order ID', key: 'id' },
            { name: 'Email', key: 'email' },
            { name: 'Quantity', key: 'quantity' },
            {
                name: 'Subtotal',
                key: 'subtotalInclTax',
            },
            {
                name: 'Subtotal (excl. tax)',
                key: 'subtotalExclTax',
                features: ['OrdersListPricesExcludingTax'],
            },
            { name: 'Fees', key: 'bookingFeesInclTax', features: ['BookingFees'] },
            {
                name: 'Fees (excl. tax)',
                key: 'bookingFeesExclTax',
                features: ['BookingFees', 'OrdersListPricesExcludingTax'],
            },
            { name: 'Discount', key: 'discountAmount' },
            { name: 'Code', key: 'discount_code' },
            { name: 'Total', key: 'totalInclTax' },
            {
                name: 'Total (excl. tax)',
                key: 'totalExclTax',
                features: ['OrdersListPricesExcludingTax'],
            },
            { name: 'Sale date', key: 'order_date' },
            { name: 'Payment date', key: 'payment_date' },
            { name: 'Order status', key: 'orderStatus' },
            { name: 'Payment status', key: 'paymentStatus' },
        ],
    },
    {
        section: 'Event information',
        columns: [
            { name: 'Visit date', key: 'visit_date' },
            { name: 'Email status', key: 'emailStatus' },
            { name: 'Event time', key: 'visit_time' },
        ],
    },
    {
        section: 'Transaction information',
        columns: [
            { name: 'Reseller', key: 'reseller' },
            { name: 'Downloaded on', key: 'downloaded' },
        ],
    },
]

export const DEFAULT_VISIBLE_COLUMNS = [
    'id',
    'email',
    'quantity',
    'totalInclTax',
    'order_date',
    'visit_date',
    'orderStatus',
    'paymentStatus',
]

export const getSectionLabel = (section: string) => {
    switch (section) {
        case 'Order details':
            return t('orders.orders.ordersTable.sections.orderDetails')
        case 'Event information':
            return t('orders.orders.ordersTable.sections.eventInformation')
        case 'Transaction information':
            return t('orders.orders.ordersTable.sections.transactionInformation')
        default:
            return section
    }
}

export const getColumnName = (column: ColumnConfig) => {
    const inclTax = column.name.endsWith(' (incl. tax)')
    switch (column.key) {
        case 'location':
            return t('orders.orders.ordersTable.columns.location')
        case 'id':
            return t('orders.orders.ordersTable.columns.orderId')
        case 'email':
            return t('orders.orders.ordersTable.columns.email')
        case 'quantity':
            return t('orders.orders.ordersTable.columns.quantity')
        case 'subtotalInclTax':
            return inclTax
                ? t('orders.orders.ordersTable.columns.subtotalInclTax')
                : t('orders.orders.ordersTable.columns.subtotal')
        case 'subtotalExclTax':
            return t('orders.orders.ordersTable.columns.subtotalExclTax')
        case 'bookingFeesInclTax':
            return inclTax
                ? t('orders.orders.ordersTable.columns.feesInclTax')
                : t('orders.orders.ordersTable.columns.fees')
        case 'bookingFeesExclTax':
            return t('orders.orders.ordersTable.columns.feesExclTax')
        case 'discountAmount':
            return t('orders.orders.ordersTable.columns.discount')
        case 'discount_code':
            return t('orders.orders.ordersTable.columns.code')
        case 'totalInclTax':
            return inclTax
                ? t('orders.orders.ordersTable.columns.totalInclTax')
                : t('orders.orders.ordersTable.columns.total')
        case 'totalExclTax':
            return t('orders.orders.ordersTable.columns.totalExclTax')
        case 'order_date':
            return t('orders.orders.ordersTable.columns.saleDate')
        case 'payment_date':
            return t('orders.orders.ordersTable.columns.paymentDate')
        case 'orderStatus':
            return t('orders.orders.ordersTable.columns.orderStatus')
        case 'paymentStatus':
            return t('orders.orders.ordersTable.columns.paymentStatus')
        case 'visit_date':
            return t('orders.orders.ordersTable.columns.visitDate')
        case 'emailStatus':
            return t('orders.orders.ordersTable.columns.emailStatus')
        case 'visit_time':
            return t('orders.orders.ordersTable.columns.eventTime')
        case 'reseller':
            return t('orders.orders.ordersTable.columns.reseller')
        case 'downloaded':
            return t('orders.orders.ordersTable.columns.downloadedOn')
        default:
            return column.name
    }
}

export const ORDERS_TABLE_COLUMNS: ColumnConfig[] = ORDERS_TABLE_COLUMNS_CONFIG.flatMap((section, index) =>
    section.columns.map((column) => ({
        ...column,
        sectionKey: index,
        selected: DEFAULT_VISIBLE_COLUMNS.includes(column.key),
    })),
)

export const sanitizeColumns = (columns: ColumnConfig[], hasFeature: (feature: string) => boolean) =>
    columns
        .filter((column) => {
            if (column.features) {
                return column.features.every((feature) => hasFeature(feature))
            }
            return true
        })
        .map((column) => {
            if (column.key.includes('InclTax') && hasFeature('OrdersListPricesExcludingTax')) {
                return { ...column, name: column.name.replace(/( \(incl\. tax\))?$/, ' (incl. tax)') }
            }
            return column
        })
